import { IconButton, Box, Chip, Tooltip, CircularProgress } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DownloadIcon from '@mui/icons-material/Download';

const SELF_SLUG_COLORS = {
  lim_self_assessment: "#0F5385",
  mim_self_assessment: "#7B1FA2",
};

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const renderChips = (values, color) => {
  if (!values || values.length === 0) return "—";
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
      {values.map((v, i) => (
        <Chip key={i} label={stripHtml(v)} size="small" color={color} variant="outlined" />
      ))}
    </Box>
  );
};

// Color scheme for different assessment pill types
const PILL_COLORS = {
  // Self-assessments
  limSelf: { borderColor: "#0F5385", color: "#0F5385", backgroundColor: "#e8f4f8" },       // Dark blue
  mimSelf: { borderColor: "#7B1FA2", color: "#7B1FA2", backgroundColor: "#f3e5f5" },       // Purple
  timSelf: { borderColor: "#2E7D32", color: "#2E7D32", backgroundColor: "#e8f5e9" },       // Green
  // Reviewee (360 participant)
  revieweeLIM: { borderColor: "#1565C0", color: "#1565C0", backgroundColor: "#e3f2fd" },   // Blue
  revieweeMIM: { borderColor: "#9C27B0", color: "#9C27B0", backgroundColor: "#fce4ec" },   // Magenta
  // Reviewer
  reviewerLIM: { borderColor: "#E65100", color: "#E65100", backgroundColor: "#fff3e0" },   // Orange
  reviewerMIM: { borderColor: "#C62828", color: "#C62828", backgroundColor: "#ffebee" },   // Red
  // Default fallback
  default: { borderColor: "#616161", color: "#616161", backgroundColor: "#f5f5f5" },
};

const getSelfAssessmentStyle = (name) => {
  const lower = name.toLowerCase();
  if (lower.includes("leaders")) return PILL_COLORS.limSelf;
  if (lower.includes("managers")) return PILL_COLORS.mimSelf;
  if (lower.includes("teams")) return PILL_COLORS.timSelf;
  return PILL_COLORS.default;
};

const getReviewerLabel = (name) => {
  const lower = name.toLowerCase();
  if (lower.includes("leaders")) return "Reviewer LIM 360";
  if (lower.includes("managers")) return "Reviewer MIM 360";
  return `Reviewer ${name}`;
};

const getReviewerStyle = (name) => {
  const lower = name.toLowerCase();
  if (lower.includes("leaders")) return PILL_COLORS.reviewerLIM;
  if (lower.includes("managers")) return PILL_COLORS.reviewerMIM;
  return PILL_COLORS.default;
};

const getRevieweeLabel = (name) => {
  const lower = name.toLowerCase();
  if (lower.includes("leaders")) return "Reviewee LIM 360";
  if (lower.includes("managers")) return "Reviewee MIM 360";
  return `Reviewee ${name}`;
};

const getRevieweeStyle = (name) => {
  const lower = name.toLowerCase();
  if (lower.includes("leaders")) return PILL_COLORS.revieweeLIM;
  if (lower.includes("managers")) return PILL_COLORS.revieweeMIM;
  return PILL_COLORS.default;
};

const renderAssessmentChips = (assessments, reviewerAssessments, revieweeAssessments) => {
  const hasAssessments = assessments && assessments.length > 0;
  const hasReviewer = reviewerAssessments && reviewerAssessments.length > 0;
  const hasReviewee = revieweeAssessments && revieweeAssessments.length > 0;
  if (!hasAssessments && !hasReviewer && !hasReviewee) return "—";
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
      {hasAssessments && assessments.map((v, i) => {
        const style = getSelfAssessmentStyle(v);
        return (
          <Chip
            key={`a-${i}`}
            label={stripHtml(v)}
            size="small"
            sx={style}
            variant="outlined"
          />
        );
      })}
      {hasReviewee && revieweeAssessments.map((v, i) => {
        const style = getRevieweeStyle(v);
        return (
          <Chip
            key={`re-${i}`}
            label={getRevieweeLabel(v)}
            size="small"
            sx={style}
            variant="outlined"
          />
        );
      })}
      {hasReviewer && reviewerAssessments.map((v, i) => {
        const style = getReviewerStyle(v);
        return (
          <Chip
            key={`r-${i}`}
            label={getReviewerLabel(v)}
            size="small"
            sx={style}
            variant="outlined"
          />
        );
      })}
    </Box>
  );
};

export const getColumnsHome = (handleEdit, handleDelete, handleSendLaunch, handleSendReminder, handleDownloadSelf, downloadingKey, handleViewProfile) => [
  {
    field: "firstName",
    headerName: "First Name",
    flex: 1,
  },
  {
    field: "lastName",
    headerName: "Last Name",
    flex: 1,
  },
  {
    field: "companyName",
    headerName: "Company",
    flex: 1,
  },
  {
    field: "email",
    headerName: "Email",
    flex: 1.2,
  },
  {
    field: "assessments",
    headerName: "Assessments",
    flex: 1.3,
    sortable: false,
    filterable: false,
    renderCell: (params) => renderAssessmentChips(params.value, params.row.reviewerAssessments, params.row.revieweeAssessments),
  },
  {
    field: "teams",
    headerName: "Team",
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: (params) => renderChips(params.value, "secondary"),
  },
  {
    field: "roles",
    headerName: "Role",
    flex: 0.8,
    sortable: false,
    filterable: false,
    renderCell: (params) => renderChips(params.value, "default"),
  },
  {
    field: "source",
    headerName: "Source",
    flex: 0.7,
    renderCell: (params) => {
      const value = params.value || "Admin";
      const isLearnWorlds = value === "LearnWorlds";
      return (
        <Chip
          label={value}
          size="small"
          sx={{
            borderColor: isLearnWorlds ? "#2196f3" : "#4caf50",
            color: isLearnWorlds ? "#2196f3" : "#4caf50",
          }}
          variant="outlined"
        />
      );
    },
  },
  {
    field: "actions",
    headerName: "Actions",
    flex: handleDownloadSelf ? 1.4 : (handleSendLaunch || handleSendReminder ? 1.1 : 0.7),
    minWidth: handleDownloadSelf ? 240 : (handleSendLaunch || handleSendReminder ? 180 : 90),
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      return (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {handleViewProfile && (
            <Tooltip title="See profile">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewProfile(params.row.id);
                }}
                size="small"
                sx={{ color: "#0F5385" }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit user">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(params.row.id);
              }}
              size="small"
              sx={{ color: "orange" }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete user">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.row.id);
              }}
              size="small"
              sx={{ color: "red" }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          {handleSendLaunch && (
            <Tooltip title="Send launch email to this user">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendLaunch(params.row);
                }}
                size="small"
                sx={{ color: "#ed6c02" }}
              >
                <EmailIcon />
              </IconButton>
            </Tooltip>
          )}
          {handleSendReminder && (
            <Tooltip title="Send reminder to this user">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendReminder(params.row);
                }}
                size="small"
                sx={{ color: "#0288d1" }}
              >
                <NotificationsActiveIcon />
              </IconButton>
            </Tooltip>
          )}
          {handleDownloadSelf &&
            (params.row.completedSelfAssessments || []).map((csa) => {
              const key = `${params.row.id}::${csa.slug}`;
              const busy = downloadingKey === key;
              const color = SELF_SLUG_COLORS[csa.slug] || "#616161";
              return (
                <Tooltip key={csa.slug} title={`Download ${csa.name} PDF`}>
                  <span>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadSelf(params.row, csa);
                      }}
                      size="small"
                      disabled={busy}
                      sx={{ color }}
                    >
                      {busy ? <CircularProgress size={16} /> : <DownloadIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              );
            })}
        </Box>
      );
    },
  },
];
